import { useMemo } from 'react';
import { useQuery } from 'urql';

import {
  type CustomRoleFilter,
  ROLE_SELECTION_LIST_QUERY,
  type TalentDiscoveryRespUserV3,
} from '@revelio/data-access';

import { useSelectionLists } from '../../../shared/selection-lists/hooks/useSelectionLists';
import { normalizeForMatch } from '../utils/normalizeForMatch';
import { useLocationMapping } from './useLocationMapping';
import type { RoleSelectionData } from './useRoleMapping';
import { useRoleMapping } from './useRoleMapping';

// ── Shared types (re-exported for consumers) ──

export type GeoType = 'region' | 'country' | 'msa' | 'city';

export interface GeoLevel {
  id: number;
  type: GeoType;
  name: string;
}

export type RoleGranularity =
  | 'role_k15000'
  | 'role_k1500'
  | 'role_k150'
  | 'role_k7';

export interface RoleLevel {
  id: number;
  type: RoleGranularity;
  name: string;
  /** The levelId string for use in CustomRoleFilter entities (e.g. 'role_k150') */
  levelId: string;
}

/** Build a CustomRoleFilter from a RoleLevel (for use in GraphQL filters). */
export const buildCustomRoleFilter = (role: RoleLevel): CustomRoleFilter => ({
  taxonomyId: { default: true },
  entities: [{ id: String(role.id), levelId: role.levelId }],
});

export interface ResolvedProfileIds {
  roleId: number | null;
  roleName: string | null;
  /** All resolved role levels, from most specific to broadest */
  roleLevels: RoleLevel[];
  seniorityId: number | null;
  companyRcid: number | null;
  companyName: string | null;
  /** All resolved geo levels (city, msa, country, region) */
  geoLevels: GeoLevel[];
  /** The currently selected geo level index */
  primaryGeo: GeoLevel | null;
}

// ── Shared utilities (used by sub-hooks) ──

export const parseId = (
  id: string | number | null | undefined
): number | null => {
  if (typeof id === 'number') return id;
  if (typeof id === 'string') {
    const parsed = parseInt(id, 10);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

// ── Internal helpers ──

interface MatchableItem {
  shortName?: string | null;
  longName?: string | null;
  id?: string | number | null;
}

const findBestMatch = <T extends MatchableItem>(
  items: T[],
  searchText: string
): T | null => {
  const normalized = normalizeForMatch(searchText);
  if (!normalized) return null;

  const exact = items.find(
    (item) =>
      normalizeForMatch(item.shortName) === normalized ||
      normalizeForMatch(item.longName) === normalized
  );
  if (exact) return exact;

  const contains = items.find((item) => {
    const short = normalizeForMatch(item.shortName);
    const long = normalizeForMatch(item.longName);
    return (
      (short && (normalized.includes(short) || short.includes(normalized))) ||
      (long && (normalized.includes(long) || long.includes(normalized)))
    );
  });
  if (contains) return contains;

  return null;
};

const EMPTY_RESOLVED: ResolvedProfileIds = {
  roleId: null,
  roleName: null,
  roleLevels: [],
  seniorityId: null,
  companyRcid: null,
  companyName: null,
  geoLevels: [],
  primaryGeo: null,
};

const SENIORITY_LIST_IDS = ['seniority'] as const;
const GEO_LIST_IDS = ['country', 'msa', 'city', 'region'] as const;

/** Ordered from most specific to least specific */
const GEO_LEVELS_ORDERED: GeoType[] = ['city', 'msa', 'country', 'region'];

// ── Main hook ──

export const useResolveProfileIds = (
  user: TalentDiscoveryRespUserV3 | null
): { resolvedIds: ResolvedProfileIds; isResolving: boolean } => {
  const seniorityLists = useSelectionLists([...SENIORITY_LIST_IDS]);
  const geoLists = useSelectionLists([...GEO_LIST_IDS]);

  const [{ data: roleData, fetching: rolesFetching }] = useQuery({
    query: ROLE_SELECTION_LIST_QUERY,
    variables: { taxonomyId: { default: true } },
    pause: !user,
  });

  // Extract and memoize all role level lists for the role mapping hook
  const roleSelectionData = useMemo((): RoleSelectionData | null => {
    const sl = roleData?.selectionList;
    if (!sl?.role_k150) return null;

    const filterNulls = <T>(
      arr: ReadonlyArray<T | null> | null | undefined
    ): T[] => (arr ?? []).filter((item): item is T => item !== null);

    return {
      role_k7: filterNulls(sl.role_k7),
      role_k50: filterNulls(sl.role_k50),
      role_k150: filterNulls(sl.role_k150),
      role_k500: filterNulls(sl.role_k500),
      role_k1500: filterNulls(sl.role_k1500),
      role_k5000: filterNulls(sl.role_k5000),
      role_k15000: filterNulls(sl.role_k15000),
    };
  }, [roleData]);

  // Resolve role via the Role Mapping API (async)
  const { role: mappedRole, isLoading: isRoleMappingLoading } = useRoleMapping(
    user?.current_title,
    roleSelectionData
  );

  // Extract user location string for the location mapper
  const userLocation = useMemo(() => {
    if (!user) return null;
    const primaryWork = user.work_history?.[0];
    return user.user_location || primaryWork?.location || null;
  }, [user]);

  // Resolve geography via the Location Mapping API (async)
  const { location: mappedLocation, isLoading: isLocationMappingLoading } =
    useLocationMapping(userLocation, geoLists);

  const resolvedIds = useMemo((): ResolvedProfileIds => {
    if (!user) return EMPTY_RESOLVED;

    const primaryWork = user.work_history?.[0];
    const result: ResolvedProfileIds = {
      ...EMPTY_RESOLVED,
      roleLevels: [],
      geoLevels: [],
    };

    // Company
    if (primaryWork?.RCID && primaryWork.RCID > 0) {
      result.companyRcid = primaryWork.RCID;
      result.companyName = user.current_company || null;
    }

    // Role: use the async role mapping result
    result.roleId = mappedRole.roleId;
    result.roleName = mappedRole.roleName;
    result.roleLevels = mappedRole.roleLevels;

    // Seniority: infer from title keywords
    if (seniorityLists?.seniority) {
      const seniorityList = seniorityLists.seniority;
      const title = (user.current_title || '').toLowerCase();
      const seniorityKeywords: Record<string, string[]> = {
        'C-Suite': ['ceo', 'cfo', 'cto', 'coo', 'cmo', 'chief', 'c-suite'],
        Director: ['director', 'head of'],
        'Vice President': ['vice president', 'vp'],
        Manager: ['manager', 'lead', 'principal', 'staff'],
        Associate: ['associate', 'senior', 'sr.', 'sr '],
        Junior: ['junior', 'jr.', 'jr '],
        'Entry Level': ['intern', 'entry', 'trainee', 'graduate'],
      };

      for (const [seniorityName, keywords] of Object.entries(
        seniorityKeywords
      )) {
        if (keywords.some((kw) => title.includes(kw))) {
          const match = seniorityList.find(
            (s) =>
              normalizeForMatch(s.shortName) ===
                normalizeForMatch(seniorityName) ||
              normalizeForMatch(s.longName) === normalizeForMatch(seniorityName)
          );
          if (match) {
            const id = parseId(match.id);
            if (id !== null) {
              result.seniorityId = id;
              break;
            }
          }
        }
      }
    }

    // Geography: prefer Location Mapping API results (exact IDs) over fuzzy matching
    if (mappedLocation.geoLevels.length > 0) {
      result.geoLevels = mappedLocation.geoLevels;
    } else if (geoLists) {
      // Fallback: fuzzy matching against selection lists
      const location = user.user_location || primaryWork?.location || '';
      if (location) {
        const locationParts = [location];
        if (location.includes(',')) {
          locationParts.push(...location.split(',').map((p) => p.trim()));
        }

        for (const geoType of GEO_LEVELS_ORDERED) {
          const list = geoLists[geoType] ?? [];
          for (const part of locationParts) {
            const match = findBestMatch([...list], part);
            if (match) {
              const id = parseId(match.id);
              if (id !== null) {
                result.geoLevels.push({
                  id,
                  type: geoType,
                  name: match.longName || match.shortName || geoType,
                });
                break;
              }
            }
          }
        }
      }
    }

    if (result.geoLevels.length > 0) {
      result.primaryGeo = result.geoLevels[0] ?? null;
    }

    return result;
  }, [user, mappedRole, mappedLocation, seniorityLists, geoLists]);

  const isResolving =
    rolesFetching ||
    isRoleMappingLoading ||
    isLocationMappingLoading ||
    !seniorityLists ||
    !geoLists;

  return { resolvedIds, isResolving };
};
